import { BESTIARY_LOOT_MEMORY } from '../data/bestiary.js';
/**
 * A character's career counters (profile window). Pure bookkeeping, no game
 * effect: the zone feeds them as things happen and saves them with the
 * progress. Each helper returns a new record so a failed save never leaves
 * a half-counted one behind.
 */
export function emptyCareer() {
    return {
        kills: {},
        deaths: 0,
        tilesWalked: 0,
        playSeconds: 0,
        spellsCast: 0,
        itemsLooted: 0,
        damageDealt: 0,
        bestHit: 0,
        camps: {},
        events: 0,
        upgrades: 0,
        shefiraKills: 0,
        lentKills: {},
        lootSeen: {},
    };
}
/** A camp's chest opened after its clear (ADR 0046), counted in its biome. */
export function recordCamp(career, biome) {
    return { ...career, camps: { ...career.camps, [biome]: (career.camps[biome] ?? 0) + 1 } };
}
/** A world event won over the threshold (ADR 0050). */
export function recordEvent(career) {
    return { ...career, events: career.events + 1 };
}
/** An attempt at the smith, whatever came of it (ADR 0051). */
export function recordUpgrade(career) {
    return { ...career, upgrades: career.upgrades + 1 };
}
/**
 * Shefira slain in a raid (ADR 0068 §5): the vow's count. Her flight at the
 * end of the story is no kill and is never counted.
 */
export function recordShefiraKill(career) {
    return { ...career, shefiraKills: career.shefiraKills + 1 };
}
export function recordKill(career, mobId) {
    return { ...career, kills: { ...career.kills, [mobId]: (career.kills[mobId] ?? 0) + 1 } };
}
/** A party kill the killer did not hunt for a quest: it counts, but not towards one (ADR 0047). */
export function recordLentKill(career, mobId) {
    const lent = { ...career.lentKills, [mobId]: (career.lentKills[mobId] ?? 0) + 1 };
    return { ...recordKill(career, mobId), lentKills: lent };
}
/**
 * Items a creature dropped for this character (ADR 0093), each template once
 * and in the order first seen, up to `BESTIARY_LOOT_MEMORY` a creature.
 */
export function recordLootSeen(career, mobId, templateIds) {
    const known = career.lootSeen[mobId] ?? [];
    const fresh = [...new Set(templateIds)].filter((id) => !known.includes(id));
    const room = BESTIARY_LOOT_MEMORY - known.length;
    if (fresh.length === 0 || room <= 0)
        return career;
    return {
        ...career,
        lootSeen: { ...career.lootSeen, [mobId]: [...known, ...fresh.slice(0, room)] },
    };
}
/** A blow landed: its damage counts towards the total and may beat the record. */
export function recordHit(career, amount) {
    const dealt = Math.max(0, Math.floor(amount));
    return {
        ...career,
        damageDealt: career.damageDealt + dealt,
        bestHit: Math.max(career.bestHit, dealt),
    };
}
/**
 * Time played, credited in whole seconds. The remainder is returned so the
 * caller carries it to the next credit instead of losing a second each save.
 */
export function addPlayTime(career, ms) {
    const seconds = Math.floor(Math.max(0, ms) / 1000);
    return {
        career: { ...career, playSeconds: career.playSeconds + seconds },
        carryMs: Math.max(0, ms) - seconds * 1000,
    };
}
/** Kills in total, or only of mobs of one rank (bosses). Unknown ids count only in the total. */
export function totalKills(career, rank, mobOf) {
    let total = 0;
    for (const [id, count] of Object.entries(career.kills)) {
        if (rank === undefined || mobOf?.(id)?.rank === rank)
            total += count;
    }
    return total;
}
/** The most hunted mobs, most kills first, ties by id so the order is stable. */
export function favouritePrey(career, limit) {
    return Object.entries(career.kills)
        .filter(([, count]) => count > 0)
        .map(([mobId, count]) => ({ mobId, count }))
        .sort((a, b) => b.count - a.count || a.mobId.localeCompare(b.mobId))
        .slice(0, Math.max(0, limit));
}
//# sourceMappingURL=career.js.map